import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAppSession as getAppSessionApi,
  isNotFound,
  setAppSessionId,
  useAppSessionActivity,
  useAppSessionBroadcast,
  useAppSessionId,
  APP_SESSION_TAB_ID,
} from 'eds-app-session';
import { getAppCodeForPath } from '@/utils/get-app-code-for-path';
import { APP_SESSION_ENDPOINTS } from '@/services/app-session/app-session-config';
import { endAppSession as endAppSessionService } from '@/services/app-session/end-app-session';

export { APP_SESSION_ENDPOINTS } from '@/services/app-session/app-session-config';

export interface UseAppSessionOptions {
  /** Suspends activity monitoring while a blocking modal is open. */
  isModalOpen?: boolean;
}

export interface UseAppSession {
  appSessionId: string | null;
  appCode?: string;
  startAppSession: () => Promise<void> | void;
  endAppSession: (reason?: 'user' | 'timeout') => Promise<void> | void;
}

/**
 * Runs the app session engine inside the platform app, scoped to whichever
 * native app the current route belongs to. Idle on platform pages, and every
 * failure is logged and swallowed so session tracking never blocks access.
 */
export function useAppSession({
  isModalOpen = false,
}: UseAppSessionOptions = {}): UseAppSession {
  const { pathname } = useLocation();
  const appCode = getAppCodeForPath(pathname);
  const appSessionId = useAppSessionId();

  // Avoids re-requesting the session on every route change within one app.
  // Cleared on leaving an app so returning re-checks against the server clock.
  const bootstrappedAppCodeRef = useRef<string | undefined>(undefined);
  // Survives platform-page visits so an app-to-app switch still clears the
  // store, even when it happens by way of the home page.
  const lastAppCodeRef = useRef<string | undefined>(undefined);

  const { createAppSession, syncFromServerActivity } = useAppSessionActivity({
    createAppSessionEndpoint: APP_SESSION_ENDPOINTS.create,
    extendAppSessionEndpoint: APP_SESSION_ENDPOINTS.extend,
    logoutAppSessionEndpoint: APP_SESSION_ENDPOINTS.logout,
    appCode,
    isModalOpen,
    disableAppSessionManagement: !appCode,
  });

  // Sign out is a platform-level action, so a logout in any tab ends this
  // tab's session too. Not disabled on platform pages: a tab parked on the
  // home page still has to follow. The sending tab skips its own message.
  useAppSessionBroadcast({
    onSessionLogout: async (_reason, senderId) => {
      if (senderId === APP_SESSION_TAB_ID) {
        return;
      }

      await endAppSessionService({ broadcast: false });

      window.location.assign('/sign-in');
    },
  });

  const adoptExistingSession = useCallback(
    async (code: string) => {
      const response = await getAppSessionApi(APP_SESSION_ENDPOINTS.get, code);

      if (!response.appSessionId) {
        return false;
      }

      const createdAt = new Date(response.creationTime).getTime();
      const lastActivityAt = new Date(response.lastActivityTime).getTime();
      const hasValidLastActivity = !Number.isNaN(lastActivityAt);

      setAppSessionId(
        response.appSessionId,
        Number.isNaN(createdAt) ? undefined : createdAt,
        hasValidLastActivity ? lastActivityAt : undefined
      );

      if (hasValidLastActivity) {
        // Trust the server clock, so an already-stale session reads as idle.
        syncFromServerActivity(lastActivityAt);
      }

      return true;
    },
    [syncFromServerActivity]
  );

  useEffect(() => {
    if (!appCode) {
      // Leaving for a platform page ends nothing; the session lapses by TTL.
      bootstrappedAppCodeRef.current = undefined;
      return;
    }

    if (bootstrappedAppCodeRef.current === appCode) {
      return;
    }

    const previousAppCode = lastAppCodeRef.current;
    bootstrappedAppCodeRef.current = appCode;
    lastAppCodeRef.current = appCode;

    // The store holds a single session; its id must never cross apps.
    if (previousAppCode && previousAppCode !== appCode) {
      setAppSessionId(null);
    }

    // A 404 means this app has no session yet, so create one. Anything else is
    // a service failure — do not pile a create onto a struggling service.
    const bootstrap = async () => {
      let needsNewSession = false;

      try {
        needsNewSession = !(await adoptExistingSession(appCode));
      } catch (error: unknown) {
        if (!isNotFound(error)) {
          console.error('Failed to get app session:', error);
          return;
        }
        needsNewSession = true;
      }

      if (!needsNewSession) {
        return;
      }

      try {
        await createAppSession();
      } catch (error: unknown) {
        console.error('Failed to create app session:', error);
      }
    };

    bootstrap().catch((error: unknown) => {
      console.error('Failed to bootstrap app session:', error);
    });
  }, [appCode, adoptExistingSession, createAppSession]);

  return {
    appSessionId,
    appCode,
    startAppSession: createAppSession,
    endAppSession: (reason: 'user' | 'timeout' = 'user') =>
      endAppSessionService({ reason }),
  };
}
